/**
 * Allocates selected travellers (employees and guests) to rooms.
 *
 * Distribution logic:
 * - Uses the adult count from each room's occupancy to determine allocation
 * - Employees are assigned first, then guests fill remaining spots
 * - Travellers are distributed sequentially across rooms based on adult capacity
 *
 * @example
 * // rooms with adults: [2, 1, 1], 3 employees, 1 guest -> room 0: [emp1, emp2], room 1: [emp3], room 2: [guest1]
 * // rooms with adults: [1, 1, 1, 1], 4 employees -> room 0: [emp1], room 1: [emp2], room 2: [emp3], room 3: [emp4]
 */
export function allocateTravellersToRooms<T extends App.Occupants | App.RoomOccupants>(
  rooms: Array<T>,
  selectedTravellerEmployeeIds: Array<string> | undefined,
  selectedTravellerGuestCount: number | undefined,
): Array<T & { employeeIds?: Array<string>; guestCount?: number }> {
  if (!selectedTravellerEmployeeIds?.length && !selectedTravellerGuestCount) {
    return rooms
  }
  const employeeIds = selectedTravellerEmployeeIds ?? []
  const guestCount = selectedTravellerGuestCount ?? 0
  const totalTravellers = employeeIds.length + guestCount

  if (totalTravellers === 0) {
    return rooms
  }

  let employeeIndex = 0
  let remainingGuests = guestCount

  return rooms.map((room) => {
    // Because we use the normaliseOccupancies function during search,
    // it's safe to use the adult count from the room's occupancy to assign employees and guests.
    const adultsInRoom = room.adults

    const roomEmployeeIds = employeeIds.slice(
      employeeIndex,
      employeeIndex + adultsInRoom
    )
    const assignedEmployees = roomEmployeeIds.length

    employeeIndex += assignedEmployees

    const neededGuests = adultsInRoom - assignedEmployees
    const roomGuestCount = Math.min(neededGuests, remainingGuests)

    remainingGuests -= roomGuestCount

    return {
      ...room,
      ...(roomEmployeeIds.length > 0 && {
        employeeIds: roomEmployeeIds,
      }),
      ...(roomGuestCount > 0 && {
        guestCount: roomGuestCount,
      }),
    }
  })
}
